import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react'

export type LoaderType = 'main' | 'secondary'

export interface LoaderHookResult<T = any> {
    data: T | undefined
    error: Error | null
    isLoading: boolean
}

export interface DataLoader<T = any> {
    id: string
    useLoad: () => LoaderHookResult<T>
    onSuccess?: (data: T) => void
    onError?: (error: Error) => void
    /** Returns user-friendly error message. Called on secondary loader failure */
    getUserMessage?: (error: Error) => string
    /** 'main': blocks app if fails. 'secondary': shows alert if fails but doesn't block. Defaults to 'secondary' */
    type?: LoaderType
}

interface LoaderResult {
    success: boolean
    error?: Error
}

interface GlobalDataLoaderProps {
    loaders: DataLoader[]
    fallback?: ReactNode
    errorFallback?: (errors: Record<string, Error>) => ReactNode
    onSecondaryError?: (id: string, error: Error, message: string) => void
    children: ReactNode
}

/**
 * Executes a single loader hook and reports results back to GlobalDataLoader.
 * Rendered as a component so each loader's hook is called in valid React context.
 */
const LoaderExecutor = <T,>({
    loader,
    onLoaded
}: {
    loader: DataLoader<T>
    onLoaded: (id: string, result: LoaderResult) => void
}) => {
    const { data, error, isLoading } = loader.useLoad()
    const reportedRef = useRef(false)

    useEffect(() => {
        if (isLoading) return

        if (data) {
            loader.onSuccess?.(data)
            if (!reportedRef.current) {
                reportedRef.current = true
                onLoaded(loader.id, { success: true })
            }
        } else if (error && !reportedRef.current) {
            reportedRef.current = true
            loader.onError?.(error)
            onLoaded(loader.id, { success: false, error })
        }
    }, [data, error, isLoading, loader, onLoaded])

    return null
}

/**
 * Generic global data loader that runs multiple loaders in parallel using React hooks.
 *
 * Main loaders (type: 'main'): If ANY fail, app doesn't load. Blocks rendering.
 * Secondary loaders (type: 'secondary'): If fail, shows alert/logs but doesn't block.
 *
 * Usage:
 * <GlobalDataLoader
 *   loaders={[
 *     { id: 'auth', useLoad: useAuthQuery, type: 'main' },
 *     { id: 'sites', useLoad: useSitesQuery, type: 'secondary', onSuccess: setSites }
 *   ]}
 *   fallback={<AppLoading />}
 *   onSecondaryError={(id, error) => showAlert(`${id} failed: ${error.message}`)}
 * >
 *   <Routes />
 * </GlobalDataLoader>
 */
export const GlobalDataLoader: React.FC<GlobalDataLoaderProps> = ({
    loaders,
    fallback = <div>Loading...</div>,
    errorFallback,
    onSecondaryError,
    children
}) => {
    const [results, setResults] = useState<Record<string, LoaderResult>>({})
    const reportedSecondaryRef = useRef<Set<string>>(new Set())

    const handleLoaded = useCallback((id: string, result: LoaderResult) => {
        setResults((prev) => ({ ...prev, [id]: result }))
    }, [])

    const mainLoaders = loaders.filter((l) => (l.type ?? 'secondary') === 'main')
    const secondaryLoaders = loaders.filter((l) => (l.type ?? 'secondary') === 'secondary')

    // Report secondary errors once
    useEffect(() => {
        secondaryLoaders.forEach((loader) => {
            const result = results[loader.id]
            if (result && !result.success && result.error && !reportedSecondaryRef.current.has(loader.id)) {
                reportedSecondaryRef.current.add(loader.id)
                const message = loader.getUserMessage?.(result.error) || result.error.message
                onSecondaryError?.(loader.id, result.error, message)
            }
        })
    }, [results, secondaryLoaders, onSecondaryError])

    const allMainDone = mainLoaders.every((l) => results[l.id])
    const mainErrors = mainLoaders.reduce<Record<string, Error>>((acc, l) => {
        const result = results[l.id]
        if (result?.error) {
            acc[l.id] = result.error
        }
        return acc
    }, {})
    const hasMainErrors = Object.keys(mainErrors).length > 0

    // If no main loaders, don't block
    const loading = mainLoaders.length > 0 && !allMainDone

    return (
        <>
            {loaders.map((loader) => (
                <LoaderExecutor key={loader.id} loader={loader} onLoaded={handleLoaded} />
            ))}
            {loading ? fallback : hasMainErrors && errorFallback ? errorFallback(mainErrors) : children}
        </>
    )
}
